<template>
    <div class="training-element-video-player w-full">
        <template v-if="variant === 'card'">
            <div
                v-if="isYouTube && src && youtubeVideoId"
                class="relative w-full overflow-hidden rounded-[6px] bg-black"
                :style="mediaBoxStyle"
            >
                <img
                    :src="`https://img.youtube.com/vi/${youtubeVideoId}/hqdefault.jpg`"
                    alt=""
                    class="youtube-card-thumb w-full h-full min-h-[120px] object-cover"
                />
                <div class="absolute inset-0 flex items-center justify-center bg-black/25 pointer-events-none">
                    <span class="youtube-card-play flex h-12 w-12 items-center justify-center rounded-full bg-[#f00]/90 shadow-md">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="white" aria-hidden="true">
                            <path d="M8 5v14l11-7z" />
                        </svg>
                    </span>
                </div>
            </div>
            <YouTube
                v-else-if="isYouTube && src"
                :src="src"
                class="w-full"
                :width="width"
                :height="height"
            />
            <div v-else-if="isMediaServer && src" class="w-full">
                <div v-if="mediaError" class="text-[#DA4167] text-sm py-2">{{ mediaError }}</div>
                <div v-else-if="mediaLoading" class="flex items-center justify-center bg-[#1e1e1e] rounded-[6px]" :style="mediaBoxStyle">
                    <span class="text-[#A3A3C2] text-sm">Завантаження відео…</span>
                </div>
                <video
                    v-else-if="mediaBlobUrl"
                    :src="mediaBlobUrl"
                    class="w-full rounded-[6px] bg-black"
                    :style="mediaBoxStyle"
                    controls
                    playsinline
                />
            </div>
            <video
                v-else-if="src"
                :src="src"
                class="w-full rounded-[6px] bg-black object-cover"
                :style="mediaBoxStyle"
                muted
                preload="metadata"
                playsinline
            />
        </template>
        <template v-else>
            <YouTube
                v-if="isYouTube && src"
                :src="src"
                class="w-full"
                :width="width"
                :height="height"
            />
            <div v-else-if="isMediaServer && src" class="w-full">
                <div v-if="mediaError" class="text-[#DA4167] text-sm py-2">{{ mediaError }}</div>
                <div v-else-if="mediaLoading" class="flex items-center justify-center bg-[#1e1e1e] rounded-[6px]" :style="mediaBoxStyle">
                    <span class="text-[#A3A3C2] text-sm">Завантаження відео…</span>
                </div>
                <video
                    v-else-if="mediaBlobUrl"
                    :src="mediaBlobUrl"
                    class="w-full rounded-[6px] bg-black"
                    :style="mediaBoxStyle"
                    controls
                    playsinline
                />
            </div>
            <video
                v-else-if="src"
                :src="src"
                class="w-full rounded-[6px] bg-black"
                :style="mediaBoxStyle"
                controls
                playsinline
            />
        </template>
    </div>
</template>

<script>
    import { ref, computed, watch, onBeforeUnmount } from 'vue'
    import YouTube from 'vue3-youtube'
    import { isYouTubeVideoUrl, isMediaServerVideoUrl, getYouTubeVideoId, resolveMediaFileDownloadUrl } from '@/utils/trainingElementVideo'
    import { sendApiRequest } from '@/services/apiRequests'

    export default {
        name: 'TrainingElementVideoPlayer',

        components: { YouTube },

        props: {
            src: {
                type: String,
                default: ''
            },
            width: {
                type: String,
                default: '100%'
            },
            height: {
                type: String,
                default: '315'
            },
            /** full: default player; card: YouTube as static thumb on grids; media server still loads blob + controls */
            variant: {
                type: String,
                default: 'full',
                validator: (v) => ['full', 'card'].includes(v)
            }
        },

        setup(props) {
            const mediaBlobUrl = ref('')
            const mediaLoading = ref(false)
            const mediaError = ref('')

            const isYouTube = computed(() => isYouTubeVideoUrl(props.src))
            const isMediaServer = computed(() => isMediaServerVideoUrl(props.src))
            const youtubeVideoId = computed(() =>
                isYouTube.value && props.src ? getYouTubeVideoId(props.src) : null
            )

            const mediaBoxStyle = computed(() => ({
                width: props.width,
                maxHeight: typeof props.height === 'string' && props.height.includes('px')
                    ? props.height
                    : props.height + 'px',
                minHeight: '120px'
            }))

            const revokeBlob = () => {
                if (mediaBlobUrl.value) {
                    URL.revokeObjectURL(mediaBlobUrl.value)
                    mediaBlobUrl.value = ''
                }
            }

            const loadMediaServerVideo = async (url) => {
                revokeBlob()
                mediaError.value = ''
                if (!url) return

                const absoluteUrl = resolveMediaFileDownloadUrl(url)
                mediaLoading.value = true
                try {
                    const response = await sendApiRequest({
                        method: 'get',
                        url: absoluteUrl,
                        responseType: 'blob'
                    })
                    const status = response?.status
                    const data = response?.data
                    if (status !== 200 || !(data instanceof Blob)) {
                        throw new Error(status ? `HTTP ${status}` : 'Немає відповіді')
                    }
                    if (data.size === 0) {
                        throw new Error('Порожня відповідь')
                    }
                    const looksJson = data.type && data.type.includes('json')
                    if (looksJson) {
                        const text = await data.text()
                        let detail = ''
                        try {
                            detail = JSON.parse(text).detail || text.slice(0, 120)
                        } catch {
                            detail = text.slice(0, 120)
                        }
                        throw new Error(detail || 'Помилка API')
                    }
                    mediaBlobUrl.value = URL.createObjectURL(data)
                } catch (e) {
                    console.error('Media server video load failed:', absoluteUrl, e)
                    let extra = ''
                    if (e?.response?.status) {
                        extra = ` (HTTP ${e.response.status})`
                    } else if (e?.message) {
                        extra = ` (${String(e.message).slice(0, 160)})`
                    }
                    mediaError.value = 'Не вдалося відтворити відео з медіа-сервера' + extra
                } finally {
                    mediaLoading.value = false
                }
            }

            watch(
                () => [props.src, props.variant],
                ([url]) => {
                    if (isMediaServerVideoUrl(url)) {
                        loadMediaServerVideo(url)
                    } else {
                        revokeBlob()
                        mediaError.value = ''
                        mediaLoading.value = false
                    }
                },
                { immediate: true }
            )

            onBeforeUnmount(revokeBlob)

            return {
                variant: computed(() => props.variant),
                isYouTube,
                isMediaServer,
                youtubeVideoId,
                mediaBlobUrl,
                mediaLoading,
                mediaError,
                mediaBoxStyle
            }
        }
    }
</script>
